import { useCallback, useEffect, useMemo, useReducer } from 'react';

import {
  heaterEditorReducer,
  initialEditorState,
  MODES,
} from './heaterEditorReducer';
import { computeHeaterChanges } from './heaterChanges';

// Changes identity whenever the SSR's heaters change underneath the form.
const signatureOf = (specs) =>
  (Array.isArray(specs) ? specs : [])
    .map((spec) => spec?.partNumber ?? '')
    .join('|');

export const useHeaterEditor = (specs) => {
  const [state, dispatch] = useReducer(
    heaterEditorReducer,
    specs,
    initialEditorState
  );

  const signature = signatureOf(specs);

  // The reducer ignores this while in EDIT or ADD, so in-progress work is safe.
  useEffect(() => {
    dispatch({ type: 'syncFromSpecs', specs });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature]);

  const enterEdit = useCallback(
    () => dispatch({ type: 'enterEdit', specs }),
    [specs]
  );
  const enterAdd = useCallback(
    () => dispatch({ type: 'enterAdd', specs }),
    [specs]
  );
  const cancel = useCallback(
    () => dispatch({ type: 'cancel', specs }),
    [specs]
  );
  const setPartNumber = useCallback(
    (index, partNumber) =>
      dispatch({ type: 'setPartNumber', index, partNumber }),
    []
  );
  const removeRow = useCallback(
    (index) => dispatch({ type: 'removeRow', index }),
    []
  );

  const changes = useMemo(() => {
    if (state.mode === MODES.VIEW) {
      return { toAdd: [], toDelete: [] };
    }
    return computeHeaterChanges(
      (Array.isArray(specs) ? specs : []).map((spec) => spec?.partNumber),
      state.rows.map((row) => row.partNumber)
    );
  }, [state.mode, state.rows, specs]);

  return {
    mode: state.mode,
    rows: state.rows,
    changes,
    hasChanges: changes.toAdd.length > 0 || changes.toDelete.length > 0,
    enterEdit,
    enterAdd,
    cancel,
    setPartNumber,
    removeRow,
  };
};
